import {
  Button,
  Card,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { profileApi } from '../../../api/profile'
import {
  useGetStaffPlatformReportsQuery,
  useLazyGetStaffPlatformReportsQuery,
  useUpdateStaffPlatformReportMutation,
} from '../../../api/platformReports'
import type { IStaffPlatformReportResource } from '../../../api/types/platformReports'
import { PLATFORM_REPORT_REASON_LABELS } from '../../../api/types/platformReports'
import { routesProfile } from '../../../consts'
import { extractApiErrorMessage } from '../../../utils/api'

const PAGE_LIMIT = 20

export const StaffPlatformReportsPage = () => {
  useLayoutEffect(() => {
    document.title = 'Жалобы платформы'
  }, [])

  const { data: me, isLoading: meLoading } = profileApi.useGetProfileQuery()
  const isStaff = me?.is_platform_staff === true

  const {
    data: firstPage,
    isLoading,
    isError,
    isFetching: isFirstFetching,
  } = useGetStaffPlatformReportsQuery({ status: 'open', limit: PAGE_LIMIT }, { skip: !isStaff })

  const [fetchMore, moreState] = useLazyGetStaffPlatformReportsQuery()
  const [updateReport, updateState] = useUpdateStaffPlatformReportMutation()

  const [items, setItems] = useState<IStaffPlatformReportResource[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<number, string>>({})

  useEffect(() => {
    if (!firstPage) return
    setItems(firstPage.items)
    setNextCursor(firstPage.nextCursor)
    setNotes((prev) => {
      const next = { ...prev }
      for (const item of firstPage.items) {
        if (next[item.id] === undefined) {
          next[item.id] = item.staff_note ?? ''
        }
      }
      return next
    })
  }, [firstPage])

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor) return
    try {
      const page = await fetchMore({
        status: 'open',
        cursor: nextCursor,
        limit: PAGE_LIMIT,
      }).unwrap()
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id))
        const appended = page.items.filter((item) => !seen.has(item.id))
        return [...prev, ...appended]
      })
      setNextCursor(page.nextCursor)
      setNotes((prev) => {
        const next = { ...prev }
        for (const item of page.items) {
          if (next[item.id] === undefined) {
            next[item.id] = item.staff_note ?? ''
          }
        }
        return next
      })
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось загрузить ещё')
    }
  }, [fetchMore, nextCursor])

  if (meLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!isStaff) {
    return <Navigate to={`/${routesProfile.SETTINGS.path}`} replace />
  }

  const handleStatus = async (
    reportId: number,
    status: 'reviewed' | 'dismissed' | 'actioned',
  ) => {
    const staffNote = (notes[reportId] ?? '').trim()
    try {
      await updateReport({
        reportId,
        body: {
          status,
          ...(staffNote ? { staff_note: staffNote } : {}),
        },
      }).unwrap()
      setItems((prev) => prev.filter((item) => item.id !== reportId))
      toast.success('Статус обновлён')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось обновить статус')
    }
  }

  const busy = updateState.isLoading || moreState.isFetching || isFirstFetching

  return (
    <Stack pt={{ xs: '15px', md: '50px' }} alignItems="center" px={1.5} pb={4}>
      <Stack sx={{ maxWidth: 840, width: '100%' }} gap={2}>
        <Typography variant="h1">Жалобы платформы</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          Очередь open. Смена статуса не удаляет контент автоматически.
        </Typography>

        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : null}

        {isError ? (
          <Typography color="error">Не удалось загрузить очередь</Typography>
        ) : null}

        {!isLoading && !isError && items.length === 0 ? (
          <Typography variant="black-14-light" color="text.secondary">
            Открытых жалоб нет.
          </Typography>
        ) : null}

        {items.map((item) => {
          const reporterName =
            item.reporter?.fullname?.trim() ||
            item.reporter?.shortname?.trim() ||
            (item.reporter?.slug ? `@${item.reporter.slug}` : `ID ${item.reporter?.id ?? '—'}`)
          const reasonLabel =
            PLATFORM_REPORT_REASON_LABELS[item.reason_code] ?? item.reason_code
          return (
            <Card key={item.id} variant="primaryAdaptiveCard" sx={{ p: 2 }}>
              <Stack gap={1.5}>
                <Typography variant="black-14-medium">
                  #{item.id} · {item.target_type} #{item.target_id} · {reasonLabel}
                </Typography>
                <Typography variant="black-12-light" color="text.secondary">
                  От: {reporterName} · {new Date(item.created_at).toLocaleString()}
                </Typography>
                {item.message ? (
                  <Typography variant="black-14-light" sx={{ whiteSpace: 'pre-wrap' }}>
                    {item.message}
                  </Typography>
                ) : null}
                <TextField
                  label="Примечание staff"
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                  value={notes[item.id] ?? ''}
                  onChange={(e) =>
                    setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))
                  }
                  inputProps={{ maxLength: 2000 }}
                  disabled={updateState.isLoading}
                />
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    variant="follow"
                    size="small"
                    disabled={busy}
                    onClick={() => void handleStatus(item.id, 'reviewed')}
                  >
                    Reviewed
                  </Button>
                  <Button
                    variant="follow"
                    size="small"
                    disabled={busy}
                    onClick={() => void handleStatus(item.id, 'dismissed')}
                  >
                    Dismissed
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    disabled={busy}
                    onClick={() => void handleStatus(item.id, 'actioned')}
                  >
                    Actioned
                  </Button>
                </Stack>
              </Stack>
            </Card>
          )
        })}

        {nextCursor ? (
          <Button
            variant="follow"
            disabled={busy}
            onClick={() => void handleLoadMore()}
            sx={{ alignSelf: 'center' }}
          >
            {moreState.isFetching ? 'Загрузка…' : 'Загрузить ещё'}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  )
}
